import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FaqAccordion from "@/components/feature/FaqAccordion";
import usePageMeta from "@/hooks/usePageMeta";
import { faqs } from "@/mocks/siteData";

const extraQuestions = [
  {
    q: "「翻车指数」是怎么算的？",
    a: "它由三个维度综合而成：事件的传播范围、公开信息的完整程度，以及暴露出的流程问题严重性。指数只用于帮助排序，不代表对当事人的评价。",
  },
  {
    q: "页面底部标注的来源和时间有什么用？",
    a: "它记录了这篇整理最后一次核对的时间。如果之后有新的公开回应，我们会更新正文并同步修改这个时间。",
  },
];

export default function Faq() {
  const crumbs = [{ label: "首页", to: "/" }, { label: "常见问题" }];
  const allItems = [...faqs, ...extraQuestions];

  usePageMeta({
    title: "常见问题_收录范围与信息来源说明 - 直播翻车观察局",
    description:
      "关于网红翻车事件整理的常见问题：收录范围、时间线来源、图片说明、纠错反馈方式与翻车指数的计算依据，都在这里说明。",
    keywords: "网红翻车常见问题,翻车事件来源,翻车指数,内容说明,纠错反馈",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "FAQPage",
          mainEntity: allItems.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: crumbs.map((crumb, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: crumb.label,
            item: crumb.to ? `https://fanche-observer.example${crumb.to}` : undefined,
          })),
        },
      ],
    },
  });

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-8 pb-10 md:pb-12 bg-background-100 border-b border-background-200">
        <Breadcrumb items={crumbs} />
        <div className="mt-6 max-w-3xl">
          <p className="flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-primary-600 mb-4 uppercase">
            <span className="w-6 h-px bg-primary-400"></span>
            FAQ
          </p>
          <h1 className="font-heading font-black text-3xl md:text-5xl leading-tight text-foreground-950">
            常见问题
          </h1>
          <p className="mt-5 text-sm md:text-lg leading-relaxed text-foreground-600">
            关于收录范围、信息来源、图片说明与反馈方式，这里集中回答。没找到答案的话，欢迎直接联系我们。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-2">
            <FaqAccordion items={allItems} />
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-32 space-y-4">
              <div className="rounded-lg p-5 bg-secondary-100 border border-secondary-200">
                <h2 className="font-heading font-semibold text-sm text-secondary-950 flex items-center gap-2">
                  <i className="ri-question-answer-line"></i>
                  还是没解决？
                </h2>
                <p className="mt-2 text-xs leading-relaxed text-secondary-900/85">
                  把具体问题写在留言里，编辑核对后会在两个工作日内回复，也可能顺手补进这份常见问题。
                </p>
                <Link
                  to="/contact"
                  className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-secondary-900 text-secondary-50 text-xs whitespace-nowrap hover:bg-secondary-950 transition-colors"
                >
                  去留言
                </Link>
              </div>

              <div className="rounded-lg p-5 bg-background-100 border border-background-200">
                <h2 className="font-heading font-semibold text-sm text-foreground-950 mb-3">继续浏览</h2>
                <ul className="space-y-2.5 text-sm">
                  <li>
                    <Link to="/category" className="flex items-center gap-2 text-foreground-700 hover:text-primary-600 transition-colors">
                      <i className="ri-list-check-2 text-foreground-400"></i>
                      全部翻车事件
                    </Link>
                  </li>
                  <li>
                    <Link to="/category?cat=classic-moments" className="flex items-center gap-2 text-foreground-700 hover:text-primary-600 transition-colors">
                      <i className="ri-movie-2-line text-foreground-400"></i>
                      名场面回放合集
                    </Link>
                  </li>
                  <li>
                    <Link to="/about" className="flex items-center gap-2 text-foreground-700 hover:text-primary-600 transition-colors">
                      <i className="ri-information-line text-foreground-400"></i>
                      关于与内容原则
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}